import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "@/components/ui";

interface NameCharacterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
  /** Modal heading (e.g., "Save Character" / "Rename Character"). */
  title: string;
  /** Submit button label (e.g., "Save" / "Rename"). */
  submitLabel: string;
  /** Pre-fills the input — auto-suggested name on save, current name on rename. */
  defaultName?: string;
  /** Spinner state while the network call is in flight. */
  isSubmitting?: boolean;
  /** Inline error message, e.g. duplicate-name 409 from the server. */
  error?: string | null;
}

/**
 * Single-input modal used wherever a character needs a name: the
 * Save-as-Character flow and the Rename flow on the detail page. Lives
 * here (not in `ui/`) because it carries character-flow specific UX
 * choices — pre-filled suggestion, max-length matching the backend,
 * Enter-to-submit, gradient submit button matching Save Character.
 */
export function NameCharacterModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  submitLabel,
  defaultName,
  isSubmitting,
  error,
}: NameCharacterModalProps) {
  const [name, setName] = useState(defaultName ?? "");

  // Re-seed the input each time the modal opens, so consecutive opens
  // pick up an updated `defaultName` (e.g., next "Character N" after
  // the user just saved another character) rather than holding stale
  // value from the previous session.
  useEffect(() => {
    if (isOpen) setName(defaultName ?? "");
  }, [isOpen, defaultName]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="max-w-md"
    >
      <div className="rounded-2xl bg-black/90 backdrop-blur-xl border border-white/[0.1] p-5 space-y-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Character name"
          className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.1] text-white outline-none focus:border-white/[0.25] transition-colors"
        />
        {error && (
          <p className="text-xs text-red-300/90">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg text-sm text-[#888] hover:text-white hover:bg-white/[0.05] disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            {submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
